/* eslint-disable @faire/enforce-internal-folder-encapsulation */
import { IInsiderTileBadgeRendererProps } from "@faire/retailer-visitor-shared/components/InsiderBadge/InsiderTileBadgeRenderer";
import { PAGE_TYPE } from "@faire/retailer-visitor-shared/consts/PAGE_TYPE";
import { PromotedBadge } from "@faire/retailer-visitor-shared/features/ADS/PromotedBadge";
import { ProductName } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/ProductInfo/ProductName";
import { ProductTileSwatches } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/ProductTileSwatches";
import { LazyDebugInfoSection } from "@faire/retailer-visitor-shared/features/MarketplaceItems/MarketplaceProduct/DebugInfo/LazyDebugInfoSection";
import {
  DiscoveryProductTileBrandInfo,
  discoveryProductTileBrandInfoParamsILeanProductTileAdapter,
} from "@faire/retailer-visitor-shared/features/MarketplaceItems/ProductDetailsTile/DiscoveryProductTileBrandInfo";
import { getImage } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ProductDetailsTile/getImage";
import { PriceText } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ProductDetailsTile/PriceText";
import { ProductTileImage } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ProductDetailsTile/ProductTileImage";
import {
  PRODUCT_TILE_SURFACE,
  TileImageOverlayContent,
} from "@faire/retailer-visitor-shared/features/MarketplaceItems/ProductDetailsTile/TileImageOverlayContent";
import {
  ProductLinkProps,
  ProductTileProps,
} from "@faire/retailer-visitor-shared/features/MarketplaceItems/ProductTile";
import { ProductTileInfo } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ProductTileInfo";
import { usePrefetchOnHover } from "@faire/retailer-visitor-shared/hooks/pdpPrefetching/usePDPPrefetchOnHover";
import { useProductTileViewImpression } from "@faire/retailer-visitor-shared/hooks/ProductTileImpression/useProductTileViewImpression";
import { useRetailer } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailer";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { assignSettingOnEvent } from "@faire/retailer-visitor-shared/settings/assignSettingOnEvent";
import { useIsLoggedOutProductTilePriceV3 } from "@faire/retailer-visitor-shared/settings/useSettingLoggedOutProductTilePrice";
import { useUserStore } from "@faire/retailer-visitor-shared/stores/domain/useUserStore";
import { getLocation } from "@faire/web--source/common/globals/getLocation";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useEffectEvent } from "@faire/web--source/ui/hooks/useEffectEvent";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import { trackEventsUntrackedproducttileView } from "@faire/web-api--source/events/events/view/untrackedproducttile";
import { IBadge } from "@faire/web-api--source/indigofair/badges/IBadge";
import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import * as React from "react";
import { Suspense, useRef } from "react";
import styled from "styled-components";

import { UnlockWholesalePriceLink } from "../ComposableProductTile/__internal__/__internal__/components/ProductInfo/PriceText/UnlockWholesalePriceLink";

export interface IProductDetailsTileProps
  extends Pick<
    ProductTileProps,
    | "imageDimension"
    | "updateCartFn"
    | "onChooseOption"
    | "data-test-id"
    | "isSubscriedToBackInStock"
    | "deleteBackInStockNotification"
  > {
  productTile: ILeanProductTile | undefined;
  showVerticalImagery?: boolean;
  brand?: ILeanProductTile.IBrand;
  disableEventBadge?: boolean;
  generateBrandUrl?: (brandToken: string) => string | undefined;
  onBrandLinkClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  productLinkProps?: ProductLinkProps;
  onTopLeftBadgeVisible?: (tileBadge?: IBadge) => void;
  onProductLinkClick?: (
    productTile: ILeanProductTile,
    event: React.MouseEvent<HTMLElement>
  ) => void;
  onTouchStart?: () => void;
  onTouchStartPrefetch?: () => void;
  onAuxClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onHeartClicked?: () => void;
  onSaveToBoard?: (boardToken?: string) => void;
  onRemoveFromBoard?: (boardToken?: string) => void;
  onQuickAdd?: (newQuantity?: number) => void;
  onQuickAddSubmit?: (newQuantity?: number) => void;
  onChooseOptionQuantityUpdate?: (newQuantity?: number) => void;
  shouldShowPromotedBadge?: boolean;
  altText?: string;
  hideAddToCartAndNotifyMe?: boolean;
  recordAdImpression: boolean | undefined;
  pageType?: PAGE_TYPE;
  shouldHideSaveToBoard?: boolean;
  imageBreakpoints?: string;
  prefetchPDPOnMouseDown?: () => void;
}

const DiscoveryProductTileProductInfo: React.FC<{
  onLinkClickHandler: (ev: React.MouseEvent<HTMLAnchorElement>) => void;
  productTile:
    | Pick<
        ProductTileInfo,
        | "product"
        | "min_option_wholesale_promo_price"
        | "min_option_wholesale_price"
        | "min_option_retail_price"
        | "options_details"
      >
    | undefined;

  isLoggedInRetailer?: boolean;
  productLinkProps?: ProductLinkProps;
  prefetchPDPOnMouseDown?: () => void;
  onTouchStartPrefetch?: () => void;
  "data-test-id"?: string;
  shouldHidePriceProminencePriceTestV3?: boolean;
}> = ({
  onLinkClickHandler,
  productTile,
  productLinkProps,
  isLoggedInRetailer,
  "data-test-id": dataTestId,
  prefetchPDPOnMouseDown,
  onTouchStartPrefetch,
  shouldHidePriceProminencePriceTestV3,
}) => {
  return (
    <div tabIndex={0}>
      <Link
        onClick={onLinkClickHandler}
        onMouseDown={prefetchPDPOnMouseDown}
        data-test-id={dataTestId}
        onTouchStart={onTouchStartPrefetch}
        to={productLinkProps?.productUrl ?? ""}
        replace={productLinkProps?.replace}
      >
        {isLoggedInRetailer ? <PriceText productTile={productTile} /> : null}
        <ProductName productName={productTile?.product?.name ?? ""} />
        {isLoggedInRetailer || shouldHidePriceProminencePriceTestV3 ? null : (
          <PriceText productTile={productTile} />
        )}
        <ProductTileSwatches optionDetails={productTile?.options_details} />
      </Link>
    </div>
  );
};

export const DiscoveryProductTile: React.FC<IProductDetailsTileProps> = (
  props
) => {
  const {
    productTile,
    imageDimension,
    showVerticalImagery,
    brand,
    updateCartFn,
    disableEventBadge,
    generateBrandUrl,
    onBrandLinkClick,
    onProductLinkClick,
    onTouchStart,
    onHeartClicked,
    onTouchStartPrefetch,
    onSaveToBoard,
    onRemoveFromBoard,
    onQuickAdd,
    onQuickAddSubmit,
    onAuxClick,
    onChooseOption,
    onChooseOptionQuantityUpdate,
    productLinkProps,
    onTopLeftBadgeVisible,
    shouldShowPromotedBadge,
    altText,
    hideAddToCartAndNotifyMe,
    recordAdImpression,
    shouldHideSaveToBoard,
    imageBreakpoints,
    prefetchPDPOnMouseDown,
  } = props;

  const { retailerToken } = useRetailer();
  const location = getLocation();
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const shouldHidePriceProminencePriceTestV3 =
    useIsLoggedOutProductTilePriceV3();

  React.useEffect(() => {
    if (!recordAdImpression) {
      trackEventsUntrackedproducttileView(
        retailerToken ?? "",
        productTile?.product?.token ?? "",
        location?.pathname ?? "" + location?.search ?? ""
      );
    }
  }, [
    recordAdImpression,
    productTile?.product?.token,
    retailerToken,
    location?.pathname,
    location?.search,
  ]);

  const trackProductTileProps = useProductTileViewImpression({
    productToken: productTile?.product?.token,
    promotedBy: productTile?.product?.promoted_by,
    impressionId: productTile?.product?.impression_id,
    turnOffTracking: !recordAdImpression,
  });
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const isLoaded = !!productTile;
  const { isEmployee, isImpersonating } = useUserStore([
    "isEmployee",
    "isImpersonating",
  ]);

  const renderDebugInfoSection = () => {
    if (!productTile?.product?.token) {
      return null;
    }
    if (!(isEmployee || isImpersonating)) {
      return null;
    }

    return (
      <Suspense fallback={null}>
        <LazyDebugInfoSection
          productToken={productTile.product.token}
          promotedBy={productTile.product.promoted_by}
          debugInfo={productTile.retrieval_and_ranking_debug_info}
        />
      </Suspense>
    );
  };

  const settingsAssignmentRef = useDoActionWhenVisible(() => {
    productTile?.settings_to_assign_on_view?.forEach((setting) => {
      assignSettingOnEvent(setting);
    });
  });

  const onLinkClickHandler = useEffectEvent(
    (ev: React.MouseEvent<HTMLAnchorElement>) => {
      if (productTile) {
        onProductLinkClick?.(productTile, ev);
      }
    }
  );

  const { onMouseEnter, onMouseLeave } = usePrefetchOnHover({
    productToken: productTile?.product?.token,
    bestImage: productTile?.best_image,
    images: productTile?.images ?? [],
    brandToken: brand?.token,
  });

  const insiderBadgeProps: IInsiderTileBadgeRendererProps = {
    insiderBenefits: brand?.insider_benefits,
    insiderLabelText: brand?.insider_label?.text,
  };

  const brandInfoComponent =
    brand == null ? null : (
      <DiscoveryProductTileBrandInfo
        brand={discoveryProductTileBrandInfoParamsILeanProductTileAdapter(
          brand
        )}
        isLoggedInRetailer={isLoggedInRetailer}
        onBrandLinkClick={onBrandLinkClick}
        generateBrandUrl={generateBrandUrl}
        data-test-id={props["data-test-id"]}
        productLinkProps={productLinkProps}
        isLoaded={isLoaded}
        insiderBadgeProps={insiderBadgeProps}
        avgBrandReviewRating={productTile?.product?.avg_brand_review_rating}
        brandReviewCount={productTile?.product?.brand_review_count}
        brandPromoText={productTile?.brand_promo_text}
        isHighlightedProductForPromotionalEvent={
          productTile?.is_highlighted_product_for_promotional_event
        }
        onLinkClickHandler={onLinkClickHandler}
        prefetchPDPOnMouseDown={prefetchPDPOnMouseDown}
        onTouchStartPrefetch={onTouchStartPrefetch}
        shouldHidePriceProminencePriceTestV3={
          shouldHidePriceProminencePriceTestV3
        }
      />
    );

  return (
    // trackProductTileProps should ALWAYS wrap the outermost div of the product tile
    <Container
      {...trackProductTileProps}
      onAuxClick={onAuxClick}
      ref={(element) => {
        trackProductTileProps?.ref?.(element);
        settingsAssignmentRef(element);
      }}
      onTouchStart={onTouchStart}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div
        id={`product-tile-${productTile?.product?.token}`}
        data-test-id={
          isLoaded
            ? (props["data-test-id"] ?? "product-tile")
            : "product-tile-placeholder"
        }
      >
        <ProductTileImage
          productToken={productTile?.product?.token}
          image={getImage(productTile)}
          data-test-id={props["data-test-id"] ?? "product-tile-link"}
          imageAltText={altText}
          onClick={onLinkClickHandler}
          prefetchPDPOnMouseDown={prefetchPDPOnMouseDown}
          showVerticalImagery={showVerticalImagery}
          imageContainerRef={imageContainerRef}
          imageDimension={imageDimension}
          imageBreakpoints={imageBreakpoints}
          productLinkProps={productLinkProps}
          onTouchStart={onTouchStartPrefetch}
        >
          <TileImageOverlayContent
            imageContainerRef={imageContainerRef}
            productTile={productTile}
            updateCartFn={updateCartFn}
            disableEventBadge={disableEventBadge}
            onTopLeftBadgeVisible={onTopLeftBadgeVisible}
            onHeartClicked={onHeartClicked}
            onSaveToBoard={onSaveToBoard}
            onQuickAdd={onQuickAdd}
            onQuickAddSubmit={onQuickAddSubmit}
            onRemoveFromBoard={onRemoveFromBoard}
            onChooseOption={onChooseOption}
            onChooseOptionQuantityUpdate={onChooseOptionQuantityUpdate}
            hideAddToCartAndNotifyMe={hideAddToCartAndNotifyMe}
            shouldHideSaveToBoard={shouldHideSaveToBoard}
            productTileSurface={PRODUCT_TILE_SURFACE.DISCOVERY}
          />
          {renderDebugInfoSection()}
        </ProductTileImage>
        <Spacer size={{ mobileAndAbove: "1x", tabletAndAbove: 12 }} />
        <Column
          gap={{
            mobileAndAbove: 2,
            tabletAndAbove: shouldHidePriceProminencePriceTestV3 ? 2 : 4,
          }}
        >
          <DiscoveryProductTileProductInfo
            productTile={productTile}
            isLoggedInRetailer={isLoggedInRetailer}
            productLinkProps={productLinkProps}
            data-test-id={props["data-test-id"]}
            onLinkClickHandler={onLinkClickHandler}
            prefetchPDPOnMouseDown={prefetchPDPOnMouseDown}
            onTouchStartPrefetch={onTouchStartPrefetch}
            shouldHidePriceProminencePriceTestV3={
              shouldHidePriceProminencePriceTestV3
            }
          />
          {shouldHidePriceProminencePriceTestV3 ? null : <Spacer size="1/2x" />}
          {brandInfoComponent}
          {shouldShowPromotedBadge ? (
            <Link
              onClick={onLinkClickHandler}
              onMouseDown={prefetchPDPOnMouseDown}
              data-test-id={props["data-test-id"]}
              to={productLinkProps?.productUrl ?? ""}
              replace={productLinkProps?.replace}
              onTouchStart={onTouchStartPrefetch}
            >
              <PromotedBadge noPadding />
            </Link>
          ) : null}
          {shouldHidePriceProminencePriceTestV3 ? (
            <UnlockWholesalePriceLink
              onLinkClickHandler={onLinkClickHandler}
              productLinkProps={productLinkProps}
            />
          ) : null}
        </Column>
      </div>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
`;
